// Small live-animated ECG strip for the onboarding rhythm-recognition quiz.
// Uses the same sum-of-Gaussians generator as the main scenario monitor.

import { useEffect, useRef } from 'react';
import { sampleEcg, type Rhythm } from '../../lib/waveforms';

interface Props {
  rhythm: Rhythm;
  hr: number;
  width?: number;
  height?: number;
  color?: string;
}

const SAMPLE_HZ = 250;
// Sweep target: ~5 seconds of trace visible across the strip width. That's the standard
// ALS rhythm-strip span (25 mm/s on paper, ~125 mm = 5 s). We downsample 250 Hz native
// generation to PX_PER_SECOND px/s so morphology stays accurate but multiple beats fit.
const PX_PER_SECOND = 56; // 280 px ÷ 5 s

export default function RhythmStrip({ rhythm, hr, width = 280, height = 80, color = '#1aff9c' }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const w = width;
    const h = height;
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);

    const buf = new Float32Array(w);
    let bufIdx = 0;
    let simTime = 0;
    let lastFrame = 0;
    let raf = 0;
    const samplesPerPx = SAMPLE_HZ / PX_PER_SECOND; // ~4.46
    let samplesSinceLastPx = 0;
    // Track the max-absolute value seen between pixel writes so we preserve R-wave peaks
    // even when downsampling.
    let pendingPeakAbs = 0;
    let pendingValue = 0;

    function range(): { min: number; max: number } {
      return { min: -1.0, max: 2.0 };
    }

    function tick(now: number) {
      if (!ctx) return;
      const dt = lastFrame === 0 ? 16 : now - lastFrame;
      lastFrame = now;
      const samples = Math.max(1, Math.round((SAMPLE_HZ * dt) / 1000));
      for (let i = 0; i < samples; i++) {
        simTime += 1000 / SAMPLE_HZ;
        const v = sampleEcg({ t_ms: simTime, rhythm, hr, cpr_active: false, cpr_quality_pct: 0 });
        // Track the most extreme sample since the last pixel-write — protects R peaks.
        if (Math.abs(v) > pendingPeakAbs) { pendingPeakAbs = Math.abs(v); pendingValue = v; }
        samplesSinceLastPx++;
        if (samplesSinceLastPx >= samplesPerPx) {
          buf[bufIdx] = pendingValue;
          bufIdx = (bufIdx + 1) % w;
          samplesSinceLastPx -= samplesPerPx;
          pendingPeakAbs = 0;
          pendingValue = 0;
        }
      }
      ctx.fillStyle = '#0a0e14';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      const r = range();
      const cursor = bufIdx;
      ctx.beginPath();
      for (let x = 0; x < w; x++) {
        const idx = (cursor + x) % w;
        if (idx === cursor && x > 0) { ctx.stroke(); ctx.beginPath(); continue; }
        const v = buf[idx] ?? 0;
        const y = h - ((v - r.min) / (r.max - r.min)) * h;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      // sweep cursor
      ctx.fillStyle = '#ffffff60';
      ctx.fillRect(w - 1, 0, 1, h);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rhythm, hr, width, height, color]);

  return <canvas ref={ref} style={{ width, height, display: 'block' }} className="bg-ink-950 rounded" />;
}
